import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  AlertColor,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import strings from 'common/Translation/Translate';
import Notification from 'components/Notification';
import PrimaryButton from 'components/Button/PrimaryButton';
import axiosInstance from 'utils/axios';
import { privileges } from 'utils/constants/privileges';
import { URLS } from 'utils/constants/urls';
import { Privilege, User } from 'utils/redux/reducer/authentication-slice';
import { Validators } from 'utils/validators';
import { ScrollTop } from 'utils/validators/HelperFunctions';

type AdditionalPropertyRow = {
  id: string;
  property: string;
  value: string;
};

const createEmptyPropertyRow = (): AdditionalPropertyRow => ({
  id: `${Date.now()}-${Math.random()}`,
  property: '',
  value: '',
});

const parseAdditionalProperties = (rawValue?: string): AdditionalPropertyRow[] => {
  if (!rawValue || rawValue.trim() === '') {
    return [createEmptyPropertyRow()];
  }

  const rows = rawValue
    .split('|')
    .map(item => item.trim())
    .filter(Boolean)
    .map(item => {
      const separatorIndex = item.indexOf(':');
      if (separatorIndex === -1) {
        return {
          id: `${Date.now()}-${Math.random()}`,
          property: item,
          value: '',
        };
      }

      return {
        id: `${Date.now()}-${Math.random()}`,
        property: item.slice(0, separatorIndex),
        value: item.slice(separatorIndex + 1),
      };
    });

  return rows.length > 0 ? rows : [createEmptyPropertyRow()];
};

const serializeAdditionalProperties = (rows: AdditionalPropertyRow[]) =>
  rows
    .filter(row => row.property.trim() !== '' || row.value.trim() !== '')
    .map(row => `${row.property.trim()}:${row.value.trim()}`)
    .join('|');

const validationMessage = (result: false | { message?: unknown } | null | undefined): string | undefined => {
  if (!result || typeof result !== 'object') return undefined;
  const m = (result as { message?: unknown }).message;
  if (m == null) return undefined;
  if (Array.isArray(m)) return m.map(String).join(', ');
  return String(m);
};

const sectionPaperSx = {
  p: { xs: 2.5, sm: 3 },
  borderRadius: 2,
  border: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  boxShadow: (theme: Theme) =>
    theme.palette.mode === 'dark' ? '0 0 0 1px rgba(255,255,255,0.06)' : '0 1px 2px rgba(15, 23, 42, 0.04)',
};

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <Typography
    variant="subtitle1"
    sx={{
      fontWeight: 700,
      mb: 2.5,
      letterSpacing: '-0.02em',
      fontSize: '1.05rem',
    }}>
    {children}
  </Typography>
);

export const MailConfigurationTab = () => {
  const { id } = useParams() as { id?: string };
  const loggedInUser = useSelector(User) as any;
  const userPrivileges = useSelector(Privilege);
  const canEditMailConfiguration = userPrivileges.includes(privileges.edit_email_server_config);

  const [pageLoading, setPageLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [host, setHost] = useState('');
  const [port, setPort] = useState('');
  const [userName, setUserName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [propertyRows, setPropertyRows] = useState<AdditionalPropertyRow[]>([createEmptyPropertyRow()]);
  const [passwordConfigured, setPasswordConfigured] = useState<boolean | undefined>(undefined);
  const [configId, setConfigId] = useState<string | number | undefined>(undefined);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const handleCatch = useCallback((error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
    });
  }, []);

  const getAdditionalPropertiesError = useCallback((rows: AdditionalPropertyRow[]) => {
    const populatedRows = rows.filter(row => row.property.trim() !== '' || row.value.trim() !== '');

    if (populatedRows.length === 0) {
      return strings.mail_configuration_property_required_error;
    }

    if (populatedRows.some(row => row.property.trim() === '' || row.value.trim() === '')) {
      return strings.mail_configuration_property_row_error;
    }

    return '';
  }, []);

  const loadConfig = useCallback(async () => {
    if (!id || !canEditMailConfiguration) return;

    try {
      setPageLoading(true);
      const { data } = await axiosInstance.get(URLS.orgMailConfig(parseInt(id, 10)));
      setHost(data?.host ?? '');
      setPort(data?.port != null ? String(data.port) : '');
      setUserName(data?.userName ?? '');
      setPasswordConfigured(data?.passwordConfigured);
      setConfigId(data?.id);
      setPropertyRows(parseAdditionalProperties(data?.additionalProperties));
      setShowPasswordFields(false);
      setNewPassword('');
      setConfirmPassword('');
      setFieldErrors({});
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setPageLoading(false);
    }
  }, [canEditMailConfiguration, handleCatch, id]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const validate = useCallback((): boolean => {
    const next: Record<string, string> = {};

    const hostErr =
      validationMessage(Validators.validateMandatoryField(host, strings.requiredText)) ||
      validationMessage(Validators.validateString(host, '', 0, 200));
    if (hostErr) next.host = hostErr;

    const portTrim = port.trim();
    const portMandatory = validationMessage(Validators.validateMandatoryField(portTrim, strings.requiredText));
    if (portMandatory) {
      next.port = portMandatory;
    } else {
      const portIntErr = validationMessage(Validators.validateInt(portTrim, '', 1, 65535));
      if (portIntErr) next.port = portIntErr;
    }

    const userErr =
      validationMessage(Validators.validateMandatoryField(userName, strings.requiredText)) ||
      validationMessage(Validators.validateString(userName, '', 0, 200));
    if (userErr) next.userName = userErr;

    const addErr = getAdditionalPropertiesError(propertyRows);
    if (addErr) next.additionalProperties = addErr;

    if (showPasswordFields) {
      const np = newPassword.trim();
      const cp = confirmPassword.trim();
      const either = np !== '' || cp !== '';
      if (either) {
        if (np === '') next.newPassword = strings.requiredText;
        if (cp === '') next.confirmPassword = strings.requiredText;
        if (np !== '' && cp !== '' && np !== cp) {
          next.confirmPassword = strings.change_password_error_message;
        }
      }
    }

    setFieldErrors(next);
    return Object.keys(next).length === 0;
  }, [confirmPassword, getAdditionalPropertiesError, host, newPassword, port, propertyRows, showPasswordFields, userName]);

  const handleSave = async () => {
    if (!id || !canEditMailConfiguration) return;
    ScrollTop(0);
    if (!validate()) {
      setMessage({
        display: true,
        severity: 'error',
        message: strings.required_error_prefix_message,
      });
      return;
    }

    const additionalSerialized = serializeAdditionalProperties(propertyRows);
    const payload = {
      host: host.trim(),
      port: parseInt(port.trim(), 10),
      userName: userName.trim(),
      password: showPasswordFields && newPassword.trim() ? newPassword : '',
      additionalProperties: additionalSerialized,
    };

    try {
      setSaving(true);
      await axiosInstance.put(URLS.orgMailConfig(parseInt(id, 10)), payload);
      setMessage({
        display: true,
        severity: 'success',
        message: configId ? strings.common_update_message : strings.common_save_message,
      });
      await loadConfig();
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!id || !canEditMailConfiguration) return;
    ScrollTop(0);
    if (!validate()) {
      setMessage({
        display: true,
        severity: 'error',
        message: strings.required_error_prefix_message,
      });
      return;
    }

    try {
      setTesting(true);
      await axiosInstance.post(URLS.orgMailConfigTestEmail(parseInt(id, 10)));
      setMessage({
        display: true,
        severity: 'success',
        message: strings.organization_mail_configuration_test_success,
      });
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setTesting(false);
    }
  };

  const closeNotification = (value: boolean) => {
    setMessage(prev => ({ ...prev, display: value }));
  };

  const additionalPropsLiveError = getAdditionalPropertiesError(propertyRows);

  if (!canEditMailConfiguration) {
    return null;
  }

  if (pageLoading) {
    return (
      <Box py={6} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <Box py={1} sx={{ width: 1 }}>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}

      <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
        {strings.mail_configuration_subtitle}
      </Typography>

      <Stack spacing={3}>
        <Paper elevation={0} sx={sectionPaperSx}>
          <SectionTitle>{strings.smtp_settings_section_title}</SectionTitle>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label={strings.email_server_config_column_host}
                value={host}
                disabled={!canEditMailConfiguration}
                error={!!fieldErrors.host}
                helperText={fieldErrors.host}
                onChange={e => setHost(e.target.value)}
                InputProps={{
                  sx: { borderRadius: 1.5 },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={strings.email_server_config_column_port}
                value={port}
                disabled={!canEditMailConfiguration}
                error={!!fieldErrors.port}
                helperText={fieldErrors.port}
                onChange={e => setPort(e.target.value)}
                InputProps={{
                  sx: { borderRadius: 1.5 },
                  inputProps: { inputMode: 'numeric' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="caption" color="text.secondary">
                        TCP
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={strings.email_server_config_column_user_name}
                value={userName}
                disabled={!canEditMailConfiguration}
                error={!!fieldErrors.userName}
                helperText={fieldErrors.userName}
                onChange={e => setUserName(e.target.value)}
                InputProps={{ sx: { borderRadius: 1.5 } }}
              />
            </Grid>
            {!showPasswordFields && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={strings.smtp_password_label}
                  value={passwordConfigured ? '••••••••' : ''}
                  placeholder={!passwordConfigured ? strings.mail_configuration_password_not_configured : undefined}
                  disabled={!canEditMailConfiguration}
                  inputProps={{
                    autoComplete: 'off',
                    name: 'org-smtp-password-status',
                  }}
                  InputProps={{
                    readOnly: true,
                    sx: { borderRadius: 1.5 },
                    endAdornment: canEditMailConfiguration ? (
                      <InputAdornment position="end" sx={{ ml: 0.5 }}>
                        <Button variant="text" size="small" onClick={() => setShowPasswordFields(true)} sx={{ whiteSpace: 'nowrap', mr: -0.5 }}>
                          {strings.user_profile_change_password}
                        </Button>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Grid>
            )}
            {showPasswordFields && canEditMailConfiguration && (
              <>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {strings.keep_existing_password_hint}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label={strings.new_password_text}
                    value={newPassword}
                    error={!!fieldErrors.newPassword}
                    helperText={fieldErrors.newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    inputProps={{
                      autoComplete: 'new-password',
                      name: 'org-smtp-new-password',
                    }}
                    InputProps={{ sx: { borderRadius: 1.5 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label={strings.confirm_password_text}
                    value={confirmPassword}
                    error={!!fieldErrors.confirmPassword}
                    helperText={fieldErrors.confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    inputProps={{
                      autoComplete: 'new-password',
                      name: 'org-smtp-new-password-confirm',
                    }}
                    InputProps={{ sx: { borderRadius: 1.5 } }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        <Paper elevation={0} sx={sectionPaperSx}>
          <SectionTitle>{strings.advanced_properties_section_title}</SectionTitle>
          <Grid container spacing={1.5} sx={{ mb: 1.5, display: { xs: 'none', md: 'flex' } }}>
            <Grid item md={5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>
                {strings.mail_configuration_property_column}
              </Typography>
            </Grid>
            <Grid item md={5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>
                {strings.mail_configuration_value_column}
              </Typography>
            </Grid>
          </Grid>
          {propertyRows.map((row, index) => (
            <Grid container spacing={1.5} key={row.id} sx={{ mb: 1.5, alignItems: 'flex-start' }}>
              <Grid item xs={12} md={5}>
                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' }, mb: 0.5 }}>
                  {strings.mail_configuration_property_column}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={row.property}
                  disabled={!canEditMailConfiguration}
                  placeholder={`${strings.mail_configuration_property_column} ${index + 1}`}
                  onChange={event => {
                    setPropertyRows(currentRows =>
                      currentRows.map(currentRow =>
                        currentRow.id === row.id ? { ...currentRow, property: event.target.value } : currentRow,
                      ),
                    );
                  }}
                  InputProps={{ sx: { borderRadius: 1.5 } }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' }, mb: 0.5 }}>
                  {strings.mail_configuration_value_column}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={row.value}
                  disabled={!canEditMailConfiguration}
                  onChange={event => {
                    setPropertyRows(currentRows =>
                      currentRows.map(currentRow =>
                        currentRow.id === row.id ? { ...currentRow, value: event.target.value } : currentRow,
                      ),
                    );
                  }}
                  InputProps={{ sx: { borderRadius: 1.5 } }}
                />
              </Grid>
              <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', pt: { md: '8px !important' } }}>
                <IconButton
                  disabled={!canEditMailConfiguration}
                  onClick={() => {
                    setPropertyRows(currentRows => {
                      if (currentRows.length === 1) {
                        return [createEmptyPropertyRow()];
                      }
                      return currentRows.filter(currentRow => currentRow.id !== row.id);
                    });
                  }}
                  sx={{ color: 'text.secondary' }}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          {(fieldErrors.additionalProperties || additionalPropsLiveError) && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {fieldErrors.additionalProperties || additionalPropsLiveError}
            </Typography>
          )}
          {canEditMailConfiguration && (
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => setPropertyRows(currentRows => [...currentRows, createEmptyPropertyRow()])}
              sx={{ borderRadius: 1.5, mt: 0.5 }}>
              {strings.mail_configuration_add_property_button}
            </Button>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            ...sectionPaperSx,
            background: (theme: Theme) =>
              theme.palette.mode === 'dark'
                ? `linear-gradient(145deg, ${theme.palette.primary.dark}22 0%, ${theme.palette.background.paper} 40%)`
                : `linear-gradient(145deg, ${theme.palette.primary.light}14 0%, ${theme.palette.background.paper} 45%)`,
          }}>
          <SectionTitle>{strings.actions_section_title}</SectionTitle>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
            {canEditMailConfiguration && (
              <PrimaryButton
                fullWidth={false}
                variant="contained"
                onClick={() => void handleSave()}
                disabled={!canEditMailConfiguration || saving || testing}
                sx={{ borderRadius: 1.5, px: 2.5, minWidth: 148 }}>
                {saving ? <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} /> : <SaveOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />}
                {strings.mail_configuration_save_changes}
              </PrimaryButton>
            )}
            {canEditMailConfiguration && !showPasswordFields && (
              <PrimaryButton
                fullWidth={false}
                variant="contained"
                onClick={() => void handleTestEmail()}
                disabled={!canEditMailConfiguration || saving || testing}
                sx={{ borderRadius: 1.5, px: 2.5, minWidth: 168 }}>
                {testing ? <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} /> : <MailOutlineIcon sx={{ mr: 1, fontSize: 20 }} />}
                {strings.organization_mail_configuration_test_button}
              </PrimaryButton>
            )}
          </Stack>
          {canEditMailConfiguration && !showPasswordFields && (
            <Typography color="text.secondary" variant="body2" sx={{ mt: 2, lineHeight: 1.6 }}>
              {`${strings.mail_configuration_test_email_helper_prefix} ${loggedInUser?.email ?? ''}`}
            </Typography>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};
